import classNames from "classnames";
import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { AccountMenu } from "tsi-common-react/src/apps/customer-account/AccountMenu/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { Messages } from "tsi-common-react/src/components/Message/index";
import { RichNavigation } from "tsi-common-react/src/components/RichNavigation/index";
import { ValueProps } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { useMobileMenuDialog } from "tsi-common-react/src/hooks/useMobileMenuDialog";
import { RichNavMobileLayoutProvider } from "tsi-common-react/src/hooks/useRichNavViewport";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { RICH_NAVIGATION_MOBILE_PANEL_ID } from "tsi-common-react/src/models/rich-navigation";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, reverse, templates } from "@reactivated";

import { constants } from "@reactivated/constants";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { FinancingModalTriggerMinicart } from "../../apps/checkout/FinancingModalTriggerMinicart";
import { useHeaderBreakpoint } from "../../utils/useHeaderBreakpoint";
import { DesktopRichNav } from "../DesktopRichNav";
import { MainMenu } from "../MainMenu";
import { IMenuItem } from "../MainMenu/menu.interface";
import { SearchNav } from "../SearchNav";
import { SiteHeaderLanguageToggle } from "../SiteHeaderLanguageToggle";

import styles from "./index.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];
type AppSlug = (typeof AppSlug)[keyof typeof AppSlug];

type LogoVariant = {
    linkClassName: string;
    ariaLabel: string;
    logoClassName?: string;
    logoSvgClassName?: string;
    logoName: string;
    target?: string;
};

type LogoVariants = {
    [key in AppSlug]?: LogoVariant;
};

export const logoVariants = {
    tempurpedic: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Tempur-Pedic home`,
        logoClassName: "site-nav__logo",
        logoName: "tempurpedicLogo",
    },
    tempurca: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Tempur-Pedic home`,
        logoClassName: "site-nav__logo site-nav__logo--canada",
        logoName: "tempurpedicLogo",
    },
    sealywarranty: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy home`,
        logoClassName: "sealy-logo",
        logoSvgClassName: "sealy-logo",
        logoName: "sealyLogo",
    },
    sealyemployees: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy | Stearns & Foster home`,
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
    },
    sealyretail: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Sealy | Stearns & Foster home`,
        logoSvgClassName: "logo-alt",
        logoName: "sealyStearnsLogo",
    },
    stearnswarranty: {
        linkClassName: "site-nav__site-logo-link",
        ariaLabel: t`Stearns & Foster home`,
        logoSvgClassName: "site-nav__logo stearns-logo",
        logoName: "stearnsWarrantyLogo",
    },
} as const satisfies LogoVariants;

export const getLogoVariant = (appSlug: string | null): LogoVariant | null => {
    // defaults can be removed and added to variant options if needed
    const defaults = {
        target: "_top",
    };
    // If any sub-sites which will use tempurpedic logo or its own logo,
    // should be added here otherwise it will return null
    switch (appSlug) {
        case AppSlug.TEMPURWARRANTY:
        case AppSlug.EMPLOYEES:
        case AppSlug.FRIENDS:
        case AppSlug.ASSOCIATES:
        case AppSlug.SALE2:
        case AppSlug.TEMPURPEDIC:
            return { ...defaults, ...logoVariants.tempurpedic };
        case AppSlug.TEMPURCA:
            return { ...defaults, ...logoVariants.tempurca };
        case AppSlug.SEALYWARRANTY:
            return { ...defaults, ...logoVariants.sealywarranty };
        case AppSlug.SEALYEMPLOYEES:
            return { ...defaults, ...logoVariants.sealyemployees };
        case AppSlug.SEALYRETAIL:
            return { ...defaults, ...logoVariants.sealyretail };
        case AppSlug.STEARNSWARRANTY:
            return { ...defaults, ...logoVariants.stearnswarranty };
        default:
            // Prevent out of context logo rendering and flashing effect
            return null;
    }
};

// The header receives the `chrome` context processor's `site_header` props
// (rich_navigation / desktop_rich_navigation / promo_settings / top_nav_menu)
// spread by PageSkeleton, plus the per-page flash `messages` it threads in
// (adapted to the tempurpedic Message shape). See PageSkeleton for the split.
type IProps = React.ContextType<typeof Context>["chrome"]["site_header"] & {
    messages: templates.UtilityPageTemplate["page_chrome"]["messages"];
    hreflangAlternates: Immutable<
        templates.UtilityPageTemplate["page_chrome"]["hreflang_alternates"]
    >;
};

export const SiteHeader = (props: IProps) => {
    const isSSR = useIsSSR();
    const viewport = useHeaderBreakpoint();
    const [dropdownOffsetTop, setDropdownOffsetTop] = useState(0);
    const [isSiteSearchOpen, setIsSiteSearchOpen] = useState(false);
    const logoRef = useRef<HTMLDivElement | null>(null);
    const {
        promo_settings,
        messages,
        rich_navigation,
        top_nav_menu,
        desktop_rich_navigation,
        hreflangAlternates,
    } = props;

    const {
        siteHeaderRef,
        mobilePanelVisible,
        showMobileMenuDialog,
        openMobilePanel,
        closeMobilePanel,
    } = useMobileMenuDialog({
        belowMobile: viewport.belowMobile,
        hasRichNavigation: !!rich_navigation,
    });

    const homeURL = usePageURL("home");
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    const isPageInsideIFrame = isSSR ? false : isPageInsideIFrameCheck();
    const appSlug = usePageSetting("app_slug");
    const enableEcom = usePageSetting("enable_ecom");

    // Map top_nav_menu data to IMenuItem[]
    const menuItems: IMenuItem[] =
        top_nav_menu?.menu?.value && Array.isArray(top_nav_menu.menu.value)
            ? (top_nav_menu.menu.value as IMenuItem[])
            : [];

    // Render logo for tempurpedic and sub-sites
    const logoVariant = getLogoVariant(appSlug);

    const mobileRichNavigation =
        showMobileMenuDialog && rich_navigation ? rich_navigation : null;

    useEffect(() => {
        if (!logoRef.current) return;

        const window = getWindow();
        const document = getDocument();
        if (!window || !document) {
            return;
        }

        const headerElem = siteHeaderRef.current;
        const logoElem = logoRef.current?.querySelector("svg");

        if (headerElem && logoElem) {
            // Get scroll position
            const getScrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            // Calculate dropdown offset
            const headerBottom = headerElem.getBoundingClientRect().bottom;
            setDropdownOffsetTop(headerBottom + getScrollTop);
        } else {
            console.warn(
                "Header or Logo element not found during offset calculation.",
            );
        }
    }, [siteHeaderRef, logoRef]);

    const isCanada = appSlug === AppSlug.TEMPURCA;
    const promoClassNames = classNames({
        "search-nav__promo": true,
        "search-nav__promo--canada": isCanada,
    });
    const promoMobileClassNames = classNames({
        "search-nav__promo--mobile": true,
        "search-nav__promo--mobile--canada": isCanada,
        "search-nav__promo--mobile--canada--search-open":
            isCanada && isSiteSearchOpen,
    });
    const searchNavLinksClassNames = classNames({
        "search-nav__links": true,
        "search-nav__links--canada": isCanada,
        "search-nav__links--canada--search-open": isCanada && isSiteSearchOpen,
    });

    return (
        <>
            {promo_settings.show_promo_header && (
                <nav className="search-nav" role="search">
                    <div className="l-max-width">
                        <a
                            className="screen-reader-skip"
                            href="#main-content"
                        >{t`Skip to main content`}</a>
                        {promo_settings.promo_header?.value.map((promo, i) => (
                            <React.Fragment key={`key:${i}`}>
                                {promo.type === "promo" && (
                                    <>
                                        <div className={promoClassNames}>
                                            {promo.value.copy && (
                                                <RichText
                                                    html={promo.value.copy}
                                                />
                                            )}
                                        </div>
                                        <div className={promoMobileClassNames}>
                                            {promo.value.mobile_copy ? (
                                                <RichText
                                                    html={
                                                        promo.value.mobile_copy
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    {promo.value.copy && (
                                                        <RichText
                                                            html={
                                                                promo.value.copy
                                                            }
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </>
                                )}
                            </React.Fragment>
                        ))}
                        <div className={searchNavLinksClassNames}>
                            {isCanada && (
                                <SiteHeaderLanguageToggle
                                    hreflangAlternates={hreflangAlternates}
                                />
                            )}
                            <SearchNav
                                isSearchOpen={isSiteSearchOpen}
                                onOpenSearch={() => setIsSiteSearchOpen(true)}
                                onCloseSearch={() => setIsSiteSearchOpen(false)}
                            />
                        </div>
                    </div>
                </nav>
            )}

            {messages && <Messages messages={messages} />}

            <header
                className="site-header"
                ref={siteHeaderRef}
                role={showMobileMenuDialog ? "dialog" : undefined}
                aria-modal={showMobileMenuDialog ? true : undefined}
                aria-labelledby={
                    showMobileMenuDialog ? "mobile-panel-title" : undefined
                }
            >
                {showMobileMenuDialog && (
                    <span
                        id="mobile-panel-title"
                        className="ada-screenreader-only"
                    >
                        {t`Menu`}
                    </span>
                )}
                <nav className="site-nav l-max-width">
                    <div>
                        {rich_navigation && (
                            <button
                                type="button"
                                className={classNames("mobile-toggle", {
                                    open: mobilePanelVisible,
                                })}
                                onClick={
                                    mobilePanelVisible
                                        ? closeMobilePanel
                                        : (event) =>
                                              openMobilePanel(
                                                  event.currentTarget,
                                              )
                                }
                                aria-controls={
                                    mobilePanelVisible
                                        ? RICH_NAVIGATION_MOBILE_PANEL_ID
                                        : undefined
                                }
                                aria-label={
                                    mobilePanelVisible
                                        ? t`Close menu`
                                        : t`Open menu`
                                }
                            >
                                <span aria-hidden="true">
                                    {mobilePanelVisible ? (
                                        <SVGIcon name="xIcon" />
                                    ) : (
                                        <SVGIcon name="barsIcon" />
                                    )}
                                </span>
                            </button>
                        )}
                        <h1>
                            {logoVariant && (
                                <Link
                                    className={logoVariant.linkClassName}
                                    href={homeURL}
                                    target={logoVariant.target}
                                >
                                    <div ref={logoRef}>
                                        <SVGIcon
                                            name={logoVariant.logoName}
                                            className={
                                                logoVariant.logoClassName
                                            }
                                            svgClassName={
                                                logoVariant.logoSvgClassName
                                            }
                                            title={logoVariant.ariaLabel}
                                        />
                                    </div>
                                </Link>
                            )}
                        </h1>
                        <nav
                            className={classNames({
                                [styles.mainMenuContainer]: true,
                                "rich-navigation--active": !!rich_navigation,
                            })}
                        >
                            {desktop_rich_navigation &&
                            !viewport.belowMobile ? (
                                <DesktopRichNav {...desktop_rich_navigation} />
                            ) : (
                                <MainMenu
                                    menuItems={menuItems}
                                    dropdownOffsetTop={dropdownOffsetTop}
                                />
                            )}
                        </nav>
                    </div>

                    {enableEcom && (
                        <ul>
                            <li>
                                <AccountMenu />
                            </li>
                            <li>
                                <BasketMenuIcon
                                    basketURL={basketURL}
                                    checkoutURL={checkoutURL}
                                    isPageInsideIFrame={isPageInsideIFrame}
                                    basketLineVariant={
                                        BasketLineVariant.MINIMAL_ENHANCED
                                    }
                                    buildPromoComponents={(closeModal) => {
                                        if (!enableBasketPrequalSDK) {
                                            return null;
                                        }
                                        return (
                                            <FinancingModalTriggerMinicart
                                                closeModal={closeModal}
                                            />
                                        );
                                    }}
                                    isSSR={isSSR}
                                />
                            </li>
                        </ul>
                    )}
                </nav>

                {mobileRichNavigation && (
                    <div
                        id={RICH_NAVIGATION_MOBILE_PANEL_ID}
                        className="rich-navigation"
                    >
                        <RichNavMobileLayoutProvider>
                            <RichNavigation
                                suppressNavLandmark
                                openMobileContentDefaultState={true}
                                {...mobileRichNavigation}
                            />
                            <ValueProps
                                value_props={mobileRichNavigation.value_props}
                                enableValuePropsOnMobile
                            />
                        </RichNavMobileLayoutProvider>
                    </div>
                )}
            </header>
        </>
    );
};
